/**
 * ChipExamples.tsx
 * Comprehensive examples of HeroUI Chip component
 * 
 * Showcases all HeroUI Chip features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { 
  X,
  Plus,
  Check,
  Star,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Shield,
  Zap,
  Users,
  Calendar,
  Globe,
  Heart,
  Bookmark,
  Filter,
  Tag
} from 'lucide-react';


export const ChipExamples: React.FC = () => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['all']);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [closedChips, setClosedChips] = useState<string[]>([]);

  const toggleFilter = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const closeChip = (chipId: string) => {
    setClosedChips(prev => [...prev, chipId]);
  };

  const isChipClosed = (chipId: string) => closedChips.includes(chipId);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Chip Variants</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-3">Solid Variants</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip color="default">Default</Chip>
                    <Chip color="primary">Primary</Chip>
                    <Chip color="secondary">Secondary</Chip>
                    <Chip color="success">Success</Chip>
                    <Chip color="warning">Warning</Chip>
                    <Chip color="danger">Danger</Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Bordered Variants</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip variant="bordered" color="default">Default</Chip>
                    <Chip variant="bordered" color="primary">Primary</Chip>
                    <Chip variant="bordered" color="secondary">Secondary</Chip>
                    <Chip variant="bordered" color="success">Success</Chip>
                    <Chip variant="bordered" color="warning">Warning</Chip>
                    <Chip variant="bordered" color="danger">Danger</Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Flat Variants</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip variant="flat" color="default">Default</Chip>
                    <Chip variant="flat" color="primary">Primary</Chip>
                    <Chip variant="flat" color="secondary">Secondary</Chip>
                    <Chip variant="flat" color="success">Success</Chip>
                    <Chip variant="flat" color="warning">Warning</Chip>
                    <Chip variant="flat" color="danger">Danger</Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Dot Variants</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip variant="dot" color="default">Default</Chip>
                    <Chip variant="dot" color="primary">Primary</Chip>
                    <Chip variant="dot" color="secondary">Secondary</Chip>
                    <Chip variant="dot" color="success">Success</Chip>
                    <Chip variant="dot" color="warning">Warning</Chip>
                    <Chip variant="dot" color="danger">Danger</Chip>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Chip Sizes</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-3">Small Chips</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip size="sm" color="primary">Small</Chip>
                    <Chip size="sm" variant="bordered" color="success">Small Bordered</Chip>
                    <Chip size="sm" variant="dot" color="warning">Small Dot</Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Medium Chips (Default)</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip size="md" color="primary">Medium</Chip>
                    <Chip size="md" variant="bordered" color="success">Medium Bordered</Chip>
                    <Chip size="md" variant="dot" color="warning">Medium Dot</Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Large Chips</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip size="lg" color="primary">Large</Chip>
                    <Chip size="lg" variant="bordered" color="success">Large Bordered</Chip>
                    <Chip size="lg" variant="dot" color="warning">Large Dot</Chip>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Chips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <h4 className="font-semibold">Closeable Chips</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-wrap gap-3">
                  {!isChipClosed('tag1') && (
                    <Chip
                      color="primary"
                      onClose={() => closeChip('tag1')}
                      startContent={<Tag className="w-3 h-3" />}
                    >
                      Design
                    </Chip>
                  )}
                  {!isChipClosed('tag2') && (
                    <Chip
                      color="success"
                      onClose={() => closeChip('tag2')}
                      startContent={<Star className="w-3 h-3" />}
                    >
                      Featured
                    </Chip>
                  )}
                  {!isChipClosed('tag3') && (
                    <Chip
                      color="warning"
                      onClose={() => closeChip('tag3')}
                      startContent={<AlertTriangle className="w-3 h-3" />}
                    >
                      Beta
                    </Chip>
                  )}
                  {!isChipClosed('tag4') && (
                    <Chip
                      variant="bordered"
                      color="secondary"
                      onClose={() => closeChip('tag4')}
                    >
                      Marketing
                    </Chip>
                  )}
                </div>
                <p className="text-xs text-text-secondary mt-3">
                  Click the × to close chips
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="font-semibold">Selection Filters</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['all', 'active', 'completed', 'pending', 'cancelled'].map((filter) => (
                    <Chip
                      key={filter}
                      color={selectedFilters.includes(filter) ? 'primary' : 'default'}
                      variant={selectedFilters.includes(filter) ? 'solid' : 'bordered'}
                      className="cursor-pointer"
                      onClick={() => toggleFilter(filter)}
                      startContent={selectedFilters.includes(filter) ? <Check className="w-3 h-3" /> : undefined}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </Chip>
                  ))}
                </div>
                <p className="text-xs text-text-secondary">
                  Selected: {selectedFilters.join(', ')}
                </p>
              </CardBody>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Chips with Icons & Content</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-3">Start Content</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip color="success" startContent={<Check className="w-4 h-4" />}>
                      Verified
                    </Chip>
                    <Chip color="warning" startContent={<Shield className="w-4 h-4" />}>
                      Protected
                    </Chip>
                    <Chip color="primary" startContent={<Zap className="w-4 h-4" />}>
                      Premium
                    </Chip>
                    <Chip variant="bordered" color="secondary" startContent={<Users className="w-4 h-4" />}>
                      Team
                    </Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">End Content</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip color="primary" endContent={<Plus className="w-4 h-4" />}>
                      Add More
                    </Chip>
                    <Chip color="success" endContent={<TrendingUp className="w-4 h-4" />}>
                      Growing
                    </Chip>
                    <Chip color="danger" endContent={<TrendingDown className="w-4 h-4" />}>
                      Declining
                    </Chip>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-3">Both Start & End Content</p>
                  <div className="flex flex-wrap gap-3">
                    <Chip 
                      color="primary" 
                      startContent={<Calendar className="w-4 h-4" />}
                      endContent={<X className="w-3 h-3" />}
                    >
                      Scheduled
                    </Chip>
                    <Chip 
                      variant="flat"
                      color="success" 
                      startContent={<Globe className="w-4 h-4" />}
                      endContent={<Check className="w-3 h-3" />}
                    >
                      Published
                    </Chip>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Use Cases</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <h4 className="font-semibold">Position Status</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Trade Status</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip size="sm" color="success" variant="dot">Open</Chip>
                      <Chip size="sm" color="primary" variant="dot">Pending</Chip>
                      <Chip size="sm" color="warning" variant="dot">Partial Fill</Chip>
                      <Chip size="sm" color="danger" variant="dot">Stopped Out</Chip>
                      <Chip size="sm" color="default" variant="dot">Closed</Chip>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-text-secondary mb-2">P&L Status</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip color="success" startContent={<TrendingUp className="w-3 h-3" />}>
                        +$1,245
                      </Chip>
                      <Chip color="danger" startContent={<TrendingDown className="w-3 h-3" />}>
                        -$523
                      </Chip>
                      <Chip variant="flat" color="default">
                        Break Even
                      </Chip>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-text-secondary mb-2">Risk Level</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip color="success">Low Risk</Chip>
                      <Chip color="warning">Medium Risk</Chip>
                      <Chip color="danger">High Risk</Chip>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="font-semibold">Trading Strategies & Tags</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Strategy Tags</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip 
                        variant={selectedTags.includes('momentum') ? 'solid' : 'bordered'}
                        color="primary"
                        className="cursor-pointer"
                        onClick={() => toggleTag('momentum')}
                      >
                        Momentum
                      </Chip>
                      <Chip 
                        variant={selectedTags.includes('scalping') ? 'solid' : 'bordered'}
                        color="secondary"
                        className="cursor-pointer"
                        onClick={() => toggleTag('scalping')}
                      >
                        Scalping
                      </Chip>
                      <Chip 
                        variant={selectedTags.includes('swing') ? 'solid' : 'bordered'}
                        color="success"
                        className="cursor-pointer"
                        onClick={() => toggleTag('swing')}
                      >
                        Swing
                      </Chip>
                      <Chip 
                        variant={selectedTags.includes('breakout') ? 'solid' : 'bordered'}
                        color="warning"
                        className="cursor-pointer"
                        onClick={() => toggleTag('breakout')}
                      >
                        Breakout
                      </Chip>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-text-secondary mb-2">Market Conditions</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip variant="flat" color="success">Bullish</Chip>
                      <Chip variant="flat" color="danger">Bearish</Chip>
                      <Chip variant="flat" color="default">Sideways</Chip>
                      <Chip variant="flat" color="warning">Volatile</Chip>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-text-secondary mb-2">Asset Classes</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip size="sm" color="primary">Forex</Chip>
                      <Chip size="sm" color="secondary">Stocks</Chip>
                      <Chip size="sm" color="success">Crypto</Chip>
                      <Chip size="sm" color="warning">Commodities</Chip>
                      <Chip size="sm" color="danger">Options</Chip>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Chip Compositions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <h4 className="font-semibold">Trade Analysis</h4>
                  <Button variant="ghost" size="sm" className="flex-shrink-0 ml-4">
                    <Filter className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">AAPL Long</p>
                        <p className="text-xs text-text-secondary">Entry: $150.25</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge content="3" color="primary">
                        <Chip size="sm" color="success">Momentum</Chip>
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <Globe className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">EUR/USD</p>
                        <p className="text-xs text-text-secondary">Pair Analysis</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Chip size="sm" variant="dot" color="primary">Forex</Chip>
                      <Chip size="sm" variant="dot" color="warning">High Vol</Chip>
                    </div>
                  </div>
                </div>
              </CardBody>
              <CardFooter>
                <div className="flex gap-2 w-full">
                  <Button size="sm" variant="bordered" color="primary" className="flex-1">
                    Analyze More
                  </Button>
                  <Button size="sm" color="primary" className="flex-1">
                    Execute Trade
                  </Button>
                </div>
              </CardFooter>
            </Card>

            <Card shadow="lg" isHoverable>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Portfolio Summary</h4>
                  <div className="flex gap-1">
                    <Chip size="sm" color="success" variant="dot">Live</Chip>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-surface rounded-lg">
                      <div className="flex justify-center mb-2">
                        <Chip size="sm" color="success">+12.5%</Chip>
                      </div>
                      <p className="text-xs text-text-secondary">Monthly Return</p>
                    </div>
                    <div className="text-center p-3 bg-surface rounded-lg">
                      <div className="flex justify-center mb-2">
                        <Chip size="sm" color="primary">67%</Chip>
                      </div>
                      <p className="text-xs text-text-secondary">Win Rate</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Active Strategies</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip size="sm" variant="flat" color="primary">
                        <Heart className="w-3 h-3 mr-1" />
                        Momentum
                      </Chip>
                      <Chip size="sm" variant="flat" color="secondary">
                        <Bookmark className="w-3 h-3 mr-1" />
                        Value
                      </Chip>
                      <Chip size="sm" variant="flat" color="success">Swing</Chip>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};